// FamilyTreeSection.js — the family, laid out as a tree.
//
// A memorial is one life; a family tree is the constellation it belongs to.
// People are stored as a flat list under memorials/{id}/familyTree, each with
// a generational TIER rather than parent-child pointers — families think in
// rows ("her parents", "her children"), and tiers render beautifully without
// a graph-layout engine.
//
// Every person may be LINKED to another SouleMap memorial. When linked, their
// portrait and dates come from that memorial and their card becomes a doorway
// to it — which is how isolated memorials become a connected web, and how
// families discover the relatives who still need one.
//
// This component DISPLAYS the tree. Building it happens where every other
// detail of a memorial is entered — the create and edit forms — so a family
// fills the tree in once, alongside the dates and the story, rather than
// discovering an editor buried in the published page.
//
// Rows are generations, and each renders as its own horizontal rail. That
// matters at depth: eight great-great-grandparents in a single shared
// scroller would drag every other row sideways with them and burst the phone
// frame. One rail per generation keeps each row independently swipeable.

import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { db } from './firebase';
import { buildGenerationRows } from './familyTreeModel';

const yearOf = (d) => {
  if (!d) return '';
  const s = String(d);
  const m = s.match(/\b(1[0-9]{3}|20[0-9]{2})\b/);
  return m ? m[1] : '';
};

// --- One person in the tree --------------------------------------------------

function PersonCard({ person, isSelf, canEdit, onEdit }) {
  const years = [yearOf(person.birthDate), yearOf(person.deathDate)]
    .filter(Boolean).join(' – ');

  const inner = (
    <>
      <div
        className="mx-auto rounded-full overflow-hidden flex items-center justify-center"
        style={{
          width: '68px',
          height: '68px',
          border: isSelf ? '2px solid #cbab6a' : '2px solid #fff',
          boxShadow: isSelf
            ? '0 0 0 1px rgba(184,150,79,0.5), 0 0 22px rgba(255,215,140,0.45)'
            : '0 2px 10px rgba(0,0,0,0.10)',
          background: 'radial-gradient(circle at 50% 32%, #232a4d 0%, #131832 55%, #0b0e20 100%)',
        }}
      >
        {person.photoUrl ? (
          <img
            src={person.photoUrl}
            alt={person.name}
            className="w-full h-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <span
            className="text-lg font-extralight"
            style={{ color: '#f5efe2', fontFamily: 'Garamond, serif', letterSpacing: '0.06em' }}
          >
            {(person.name || '?').trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase()).join('')}
          </span>
        )}
      </div>

      <p
        className="mt-2.5 text-[13px] leading-tight font-light"
        style={{ color: isSelf ? '#96793c' : '#3f3d45' }}
      >
        {person.name}
      </p>
      <p className="text-[9.5px] uppercase mt-1" style={{ letterSpacing: '0.2em', color: '#b8964f' }}>
        {person.relation}
      </p>
      {years && <p className="text-[10px] text-gray-400 mt-0.5">{years}</p>}
      {person.memorialSlug && (
        <p className="text-[9px] uppercase mt-1" style={{ letterSpacing: '0.16em', color: '#cbab6a' }}>
          ✦ Their memorial
        </p>
      )}
    </>
  );

  const frame = {
    width: '132px',
    padding: '14px 10px 12px',
    borderRadius: '14px',
    background: isSelf ? '#fffdf7' : '#ffffff',
    border: isSelf ? '1px solid #d8c9a4' : '1px solid #eee9df',
    textAlign: 'center',
  };

  return (
    <div className="relative shrink-0">
      {person.memorialSlug ? (
        <Link to={`/memorial/${person.memorialSlug}`} style={{ ...frame, display: 'block', textDecoration: 'none' }}
          className="transition-shadow hover:shadow-[0_8px_28px_rgba(184,150,79,0.18)]">
          {inner}
        </Link>
      ) : (
        <div style={frame}>{inner}</div>
      )}

      {canEdit && (
        <button
          onClick={(e) => { e.preventDefault(); onEdit(person); }}
          aria-label={`Edit ${person.name}`}
          className="absolute -top-2 -right-2 w-6 h-6 rounded-full text-[11px] flex items-center justify-center transition-shadow hover:shadow-[0_0_12px_rgba(255,215,140,0.5)]"
          style={{ background: '#12101d', color: '#ffe2a8', border: '1px solid rgba(184,150,79,0.5)' }}
        >
          ✎
        </button>
      )}
    </div>
  );
}


function FamilyTreeSection({ memorial, memorialId, canEdit, editHref }) {
  const [people, setPeople] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!memorialId) return undefined;
    const q = query(collection(db, 'memorials', memorialId, 'familyTree'), orderBy('createdAt', 'asc'));
    const unsub = onSnapshot(
      q,
      (snap) => { setPeople(snap.docs.map((d) => ({ id: d.id, ...d.data() }))); setLoaded(true); },
      (err) => { console.warn('family tree listener:', err); setLoaded(true); }
    );
    return unsub;
  }, [memorialId]);

  // The memorial's own person, seated in their generation.
  const selfCard = useMemo(() => ({
    id: '__self__',
    name: [memorial?.firstName, memorial?.lastName].filter(Boolean).join(' '),
    relation: 'This memorial',
    photoUrl: memorial?.images?.[0] || '',
    birthDate: memorial?.birthDate || '',
    deathDate: memorial?.deathDate || '',
  }), [memorial]);

  const rows = useMemo(() => buildGenerationRows(people, selfCard), [people, selfCard]);

  // Nothing to show and nobody who could add anyone: stay out of the way.
  if (loaded && people.length === 0 && !canEdit) return null;

  return (
    <div className="bg-white p-6 md:p-10 mb-8 border border-[#eee9df]">
      <div className="text-center mb-8">
        <p className="text-[10.5px] uppercase" style={{ letterSpacing: '0.34em', color: '#b8964f' }}>
          Their People
        </p>
        <h2 className="mt-3 text-2xl md:text-3xl font-extralight text-gray-900" style={{ letterSpacing: '0.04em' }}>
          Family Tree
        </h2>
        <div
          className="mx-auto mt-4"
          style={{ width: '54px', height: '1px', background: 'linear-gradient(90deg, transparent, #cbab6a, transparent)' }}
        />
        <p className="mt-4 text-gray-500 text-sm font-light">
          The branches {memorial?.firstName ? `${memorial.firstName} belongs to` : 'they belong to'}
        </p>
      </div>

      {people.length === 0 ? (
        <div className="text-center py-6">
          <p className="text-gray-500 text-sm font-light mb-6">
            No one has been added yet — begin with {memorial?.firstName ? `${memorial.firstName}'s` : 'their'} parents or children.
          </p>
          {canEdit && editHref && (
            <Link to={editHref} className="sm-btn-primary" style={{ textDecoration: 'none' }}>
              ✦ Add their family
            </Link>
          )}
        </div>
      ) : (
        <>
          {/* The generations. Each row is a tier; the hairline above a row is
              the branch that joins it to the generation before it. */}
          {/* No shared horizontal scroller: each generation scrolls on its own,
              so a row of sixteen ancestors never drags the rest sideways. */}
          <div>
            <div>
              {rows.map((row, rowIndex) => (
                <div key={row.key}>
                  {rowIndex > 0 && (
                    <div className="flex flex-col items-center" aria-hidden="true">
                      <div style={{ width: '1px', height: '18px', background: 'linear-gradient(180deg, rgba(203,171,106,0.5), rgba(203,171,106,0.5))' }} />
                      <div style={{ width: '72%', maxWidth: '520px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(203,171,106,0.55), transparent)' }} />
                      <div style={{ width: '1px', height: '18px', background: 'linear-gradient(180deg, rgba(203,171,106,0.5), rgba(203,171,106,0.5))' }} />
                    </div>
                  )}

                  <p
                    className="text-center text-[9px] uppercase mb-1"
                    style={{ letterSpacing: '0.26em', color: 'rgba(184,150,79,0.75)' }}
                  >
                    {row.label}
                  </p>
                  {row.hint && (
                    <p className="text-center text-[9px] text-gray-400 font-light mb-2">{row.hint}</p>
                  )}
                  {!row.hint && <div className="mb-2" />}

                  <div
                    className="rail overflow-x-auto pb-2 flex items-start gap-4 md:gap-6 flex-nowrap justify-start md:justify-center"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', scrollSnapType: 'x proximity' }}
                  >
                    {row.members.map((p) => (
                      <PersonCard
                        key={p.id}
                        person={p}
                        isSelf={p.id === '__self__'}
                        canEdit={false}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {canEdit && editHref && (
            <div className="text-center mt-8">
              <Link to={editHref} className="sm-btn-primary" style={{ textDecoration: 'none' }}>
                ✦ Add or edit family
              </Link>
              <p className="mt-2 text-[11px] text-gray-400 font-light">
                The family tree is edited alongside the rest of the memorial.
              </p>
            </div>
          )}
        </>
      )}

    </div>
  );
}

export default FamilyTreeSection;
